import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useAuthStore } from './auth-store';
import { auditLogger } from '../services/auditLogger';

// Define user roles as enum for better type safety
export enum UserRole {
  TM_ADMIN = 'tm_admin',
  DISTRICT_CHAMPION = 'district_champion',
  SCHOOL_CHAMPION = 'school_champion'
}

// Define organization types as enum
export enum OrganizationType {
  DISTRICT = 'district',
  SCHOOL = 'school'
}

// Define basic permission types as enum for type safety
// Added CREATE permission to support organization creation restrictions
export enum BasicPermissionType {
  VIEW = 'view',
  EDIT = 'edit',
  DELETE = 'delete',
  CREATE = 'create' // Added to properly control district/school creation permissions
}

// Define feature-specific permissions for granular access control
export enum FeaturePermissionType {
  CREATE_SURVEY = 'create_survey',
  MANAGE_PARTICIPANTS = 'manage_participants',
  VIEW_ANALYTICS = 'view_analytics',
  WELLNESS_CENTER_ACCESS = 'wellness_center_access',
  BULK_OPERATIONS = 'bulk_operations',
  EXPORT_DATA = 'export_data',
  SEND_INVITATIONS = 'send_invitations',
  GENERATE_REPORTS = 'generate_reports'
}

// Combined permission type
export type PermissionType = BasicPermissionType | FeaturePermissionType;

// Legacy type aliases for backward compatibility
export type UserRoleType = UserRole;
export type OrganizationTypeType = OrganizationType;

// Enhanced permission configuration with feature-specific permissions
// Defines granular RBAC permissions for each user role and organization type
export const PERMISSION_CONFIG = {
  [UserRole.TM_ADMIN]: {
    districts: { 
      view: true, 
      edit: true, 
      delete: true,
      create: true, // TM admins can create new districts
      create_survey: true,
      manage_participants: true,
      view_analytics: true,
      bulk_operations: true,
      export_data: true,
      send_invitations: true,
      generate_reports: true
    },
    schools: { 
      view: true, 
      edit: true, 
      delete: true,
      create: true,
      create_survey: true,
      manage_participants: true,
      view_analytics: true,
      bulk_operations: true,
      export_data: true,
      send_invitations: true,
      generate_reports: true
    },
    features: {
      wellness_center_access: true
    },
    defaultDistrictId: null,
    defaultSchoolId: null
  },
  [UserRole.DISTRICT_CHAMPION]: {
    districts: { 
      view: 'own', 
      edit: 'own', 
      delete: false,
      create: false, // District champions cannot create new districts (security requirement)
      create_survey: 'own',
      manage_participants: 'own',
      view_analytics: 'own',
      bulk_operations: 'own',
      export_data: 'own',
      send_invitations: 'own',
      generate_reports: 'own'
    },
    schools: { 
      view: 'in_district', 
      edit: 'in_district', 
      delete: false,
      create: 'in_district', // District champions can create schools within their district
      create_survey: 'in_district',
      manage_participants: 'in_district',
      view_analytics: 'in_district',
      bulk_operations: 'in_district',
      export_data: 'in_district',
      send_invitations: 'in_district',
      generate_reports: 'in_district'
    },
    features: {
      wellness_center_access: true
    },
    defaultDistrictId: 'userOrgId',
    defaultSchoolId: null
  },
  [UserRole.SCHOOL_CHAMPION]: {
    districts: { 
      view: 'parent', 
      edit: false, 
      delete: false,
      create: false, // School champions cannot create districts
      create_survey: false,
      manage_participants: false,
      view_analytics: 'parent',
      bulk_operations: false,
      export_data: 'parent',
      send_invitations: false,
      generate_reports: 'parent'
    },
    schools: { 
      view: 'own', 
      edit: 'own', 
      delete: false,
      create: false, // School champions cannot create new schools
      create_survey: 'own',
      manage_participants: 'own',
      view_analytics: 'own',
      bulk_operations: 'own',
      export_data: 'own',
      send_invitations: 'own',
      generate_reports: 'own'
    },
    features: {
      wellness_center_access: true
    },
    defaultDistrictId: 'userParentOrgId',
    defaultSchoolId: 'userOrgId'
  }
} as const;

// Enhanced error types as enum
export enum RBACErrorCode {
  NO_ROLE = 'NO_ROLE',
  INVALID_PERMISSION = 'INVALID_PERMISSION',
  NETWORK_ERROR = 'NETWORK_ERROR',
  INITIALIZATION_ERROR = 'INITIALIZATION_ERROR',
  CACHE_ERROR = 'CACHE_ERROR',
  AUDIT_ERROR = 'AUDIT_ERROR'
}

export type RBACError = {
  code: RBACErrorCode;
  message: string;
  details?: Record<string, unknown> | string | null;
  timestamp?: number;
  userId?: string;
};

// Interface for RBAC state
interface RBACState {
  // User role and attributes
  userRole: UserRole | null;
  userOrgId: string | null;
  userParentOrgId: string | null;
  uiMode: string | null;
  
  // Default selections based on role
  defaultDistrictId: string | null;
  defaultSchoolId: string | null;
  
  // Loading states
  isLoading: boolean;
  error: RBACError | null;
  
  // Retry mechanism
  retryCount: number;
  maxRetries: number;
  
  // Actions
  initializeRBAC: () => Promise<void>;
  retryInitialization: () => Promise<void>;
  getUserRole: () => UserRole | null;
  getUserOrgId: () => string | null;
  getUserParentOrgId: () => string | null;
  canAccessOrganization: (orgId: string, type: OrganizationType) => boolean;
  canEditOrganization: (orgId: string, type: OrganizationType) => boolean;
  canDeleteOrganization: (orgId: string, type: OrganizationType) => boolean;
  canAccessFeature: (feature: FeaturePermissionType) => boolean;
  checkPermissionWithAudit: (orgId: string, type: OrganizationType, permission: PermissionType) => boolean;
  getDefaultDistrictId: () => string | null;
  getDefaultSchoolId: () => string | null;
  clearRBAC: () => void;
  resetError: () => void;
}

// Add a module-level guard for RBAC initialization
let rbacInitialized = false;

// Enhanced error handling with retry mechanism
const createRBACError = (
  code: RBACErrorCode, 
  message: string, 
  details?: unknown
): RBACError => ({
  code,
  message,
  details: details && typeof details === 'object' ? details as Record<string, unknown> : String(details || ''),
  timestamp: Date.now()
});

// Helper function to extract Cognito attributes with validation
const extractCognitoAttributes = () => {
  const user = useAuthStore.getState().user;
  if (!user?.attributes) {
    console.warn('⚠️ RBAC: No user attributes found');
    return { role: null, orgId: null, parentOrgId: null, uiMode: null };
  }

  const role = user.attributes['custom:role'] as UserRole;
  const orgId = user.attributes['custom:orgId'];
  const parentOrgId = user.attributes['custom:parentOrgId'];
  const uiMode = user.attributes['custom:uiMode'];

  // Validate role
  if (role && !Object.keys(PERMISSION_CONFIG).includes(role)) {
    console.warn('⚠️ RBAC: Invalid role found:', role);
    return { role: null, orgId: null, parentOrgId: null, uiMode: null };
  }

  // Only log if there's an issue with role extraction
  if (!role) {
    console.warn('⚠️ RBAC: No role found in Cognito attributes');
  }

  return { role, orgId, parentOrgId, uiMode };
};

// Configuration-driven permission checking with enhanced type safety
const checkPermission = (
  role: UserRole,
  orgId: string,
  type: OrganizationType,
  permission: PermissionType,
  userOrgId: string | null,
  userParentOrgId: string | null
): boolean => {
  const config = PERMISSION_CONFIG[role];
  if (!config) return false;

  // Handle feature permissions separately
  if (permission === FeaturePermissionType.WELLNESS_CENTER_ACCESS) {
    return config.features?.wellness_center_access || false;
  }

  const permissionConfig = config[type === OrganizationType.DISTRICT ? 'districts' : 'schools'];
  const permissionValue = (permissionConfig as Record<string, unknown>)[permission];

  if (typeof permissionValue === 'boolean') {
    return permissionValue;
  }

  switch (permissionValue) {
    case 'own':
      return orgId === userOrgId;
    case 'parent':
      return orgId === userParentOrgId;
    case 'in_district':
      // For schools, check if they belong to user's district
      return type === OrganizationType.SCHOOL && !!userOrgId;
    default:
      return false;
  }
};

// Helper function to determine default selections based on role
const getDefaultSelections = (role: UserRole, userOrgId: string | null, userParentOrgId: string | null) => {
  const config = PERMISSION_CONFIG[role];
  if (!config) return { defaultDistrictId: null, defaultSchoolId: null };

  const getDefaultId = (defaultKey: string | null) => {
    switch (defaultKey) {
      case 'userOrgId':
        return userOrgId;
      case 'userParentOrgId':
        return userParentOrgId;
      default:
        return null;
    }
  };

  return {
    defaultDistrictId: getDefaultId(config.defaultDistrictId),
    defaultSchoolId: getDefaultId(config.defaultSchoolId)
  };
};

export const useRBACStore = create<RBACState>()(
  devtools(
    (set, get) => ({
      // Initial state
      userRole: null,
      userOrgId: null,
      userParentOrgId: null,
      uiMode: null,
      defaultDistrictId: null,
      defaultSchoolId: null,
      isLoading: false,
      error: null,
      retryCount: 0,
      maxRetries: 3,

      // Initialize RBAC based on user attributes with retry mechanism
      initializeRBAC: async () => {
        const currentState = get();
        // Check if already initialized with a valid role
        if (rbacInitialized && currentState.userRole) {
          return;
        }
        
        const { retryCount, maxRetries } = currentState;
        // Only log if retrying after errors
        if (retryCount > 0) {
          console.log('🔄 RBAC: Retrying initialization...', { retryCount, maxRetries });
        }
        
        set({ isLoading: true, error: null });

        try {
          const { role, orgId, parentOrgId, uiMode } = extractCognitoAttributes();
          
          if (!role) {
            const error = createRBACError(RBACErrorCode.NO_ROLE, 'No user role found in Cognito attributes');
            console.warn('⚠️ RBAC:', error.message);
            set({ 
              isLoading: false, 
              error,
              retryCount: 0
            });
            rbacInitialized = false; // Reset flag to allow retry
            return;
          }

          const defaultSelections = getDefaultSelections(role, orgId || null, parentOrgId || null);

          // Only log successful initialization

          set({
            userRole: role,
            userOrgId: orgId,
            userParentOrgId: parentOrgId,
            uiMode: uiMode,
            defaultDistrictId: defaultSelections.defaultDistrictId,
            defaultSchoolId: defaultSelections.defaultSchoolId,
            isLoading: false,
            error: null,
            retryCount: 0
          }, false, 'initializeRBAC/success');

          console.log('✅ RBAC: Successfully initialized with role:', role);
          rbacInitialized = true; // Set flag only after successful initialization

        } catch (error) {
          console.log('[rbac-store][initializeRBAC] Error:', error)
          const rbacError = createRBACError(
            RBACErrorCode.INITIALIZATION_ERROR,
            'Failed to initialize RBAC system',
            error
          );
          console.error('❌ RBAC: Error initializing RBAC:', rbacError);
          
          set({ 
            isLoading: false, 
            error: rbacError,
            retryCount: retryCount + 1
          }, false, 'initializeRBAC/error');
          rbacInitialized = false; // Reset flag to allow retry
        }
      },

      // Retry initialization (simplified)
      retryInitialization: async () => {
        rbacInitialized = false; // Reset flag to allow retry
        set({ error: null, retryCount: 0 });
        await get().initializeRBAC();
      },

      // Getter methods
      getUserRole: () => get().userRole,
      getUserOrgId: () => get().userOrgId,
      getUserParentOrgId: () => get().userParentOrgId,

      // Enhanced permission check methods with configuration-driven logic
      canAccessOrganization: (orgId: string, type: OrganizationType) => {
        const state = get();
        const { userRole, userOrgId, userParentOrgId } = state;

        if (!userRole) {
          return false;
        }

        return checkPermission(userRole, orgId, type, BasicPermissionType.VIEW, userOrgId, userParentOrgId);
      },

      canEditOrganization: (orgId: string, type: OrganizationType) => {
        const state = get();
        const { userRole, userOrgId, userParentOrgId } = state;

        if (!userRole) {
          return false;
        }

        return checkPermission(userRole, orgId, type, BasicPermissionType.EDIT, userOrgId, userParentOrgId);
      },

      canDeleteOrganization: (orgId: string, type: OrganizationType) => {
        const state = get();
        const { userRole, userOrgId, userParentOrgId } = state;

        if (!userRole) {
          return false;
        }

        return checkPermission(userRole, orgId, type, BasicPermissionType.DELETE, userOrgId, userParentOrgId);
      },

      getDefaultDistrictId: () => get().defaultDistrictId,
      getDefaultSchoolId: () => get().defaultSchoolId,

      // Feature-specific permission checking with audit logging
      canAccessFeature: (feature: FeaturePermissionType) => {
        const state = get();
        const { userRole } = state;
        const user = useAuthStore.getState().user;
        const userId = user?.sub || 'unknown';

        if (!userRole) {
          auditLogger.logFeatureAccess(
            userId,
            UserRole.TM_ADMIN, // Default for logging
            feature,
            'DENIED',
            { reason: 'No user role' }
          );
          return false;
        }

        const config = PERMISSION_CONFIG[userRole];
        if (!config) {
          auditLogger.logFeatureAccess(
            userId,
            userRole,
            feature,
            'DENIED',
            { reason: 'No config found for role' }
          );
          return false;
        }

        const hasAccess = config.features?.[feature as keyof typeof config.features] || false;
        
        auditLogger.logFeatureAccess(
          userId,
          userRole,
          feature,
          hasAccess ? 'GRANTED' : 'DENIED'
        );

        return hasAccess;
      },

      // Enhanced permission checking with comprehensive audit logging
      checkPermissionWithAudit: (orgId: string, type: OrganizationType, permission: PermissionType) => {
        const state = get();
        const { userRole, userOrgId, userParentOrgId } = state;
        const user = useAuthStore.getState().user;
        const userId = user?.sub || 'unknown';

        if (!userRole) {
          // Log access denial with audit logger
          auditLogger.logPermissionCheck(
            userId,
            UserRole.TM_ADMIN, // Default for logging
            orgId,
            type,
            permission,
            'DENIED',
            { reason: 'No user role', userOrgId, userParentOrgId }
          );
          return false;
        }

        const hasPermission = checkPermission(userRole, orgId, type, permission, userOrgId, userParentOrgId);
        
        // Log permission check result
        auditLogger.logPermissionCheck(
          userId,
          userRole,
          orgId,
          type,
          permission,
          hasPermission ? 'GRANTED' : 'DENIED',
          { userOrgId, userParentOrgId, method: 'checkPermissionWithAudit' }
        );

        return hasPermission;
      },

      // Clear RBAC data
      clearRBAC: () => {
        rbacInitialized = false;
        set({
          userRole: null,
          userOrgId: null,
          userParentOrgId: null,
          defaultDistrictId: null,
          defaultSchoolId: null,
          isLoading: false,
          error: null,
          retryCount: 0
        }, false, 'clearRBAC');
      },

      // Reset error state
      resetError: () => {
        set({ error: null, retryCount: 0 }, false, 'resetError');
      },
    }),
    {
      name: 'rbac-store',
      enabled: import.meta.env.DEV // Only enable devtools in development
    }
  )
); 